import React, { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { CATEGORY_ICON } from "@/data/brand";

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeCat = params.get("category") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (activeCat) qs.set("category", activeCat);
    if (city) qs.set("city", city);
    if (q) qs.set("q", q);
    api.get(`/listings?${qs.toString()}`).then((r) => setListings(r.data)).finally(() => setLoading(false));
  }, [activeCat, q, city]);

  // Smooth-scroll to results when landing with #results (from Home search)
  useEffect(() => {
    if (location.hash === "#results") {
      const el = document.getElementById("results");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [location.hash, listings.length]);

  const setCat = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p);
    setFiltersOpen(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="listings-page">
      <header className="mb-6 md:mb-10">
        <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] md:tracking-[0.3em] text-[#FFBE00] mb-2">· Annunci</div>
        <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl leading-tight">
          Tutti i <span className="text-[#E619A8]">professionisti</span>.
        </h1>
      </header>

      {/* Mobile: top search bar + toggle for category filter */}
      <div className="md:hidden mb-4 space-y-2" data-testid="listings-mobile-filters">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <i className="fas fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm"></i>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Parola chiave…"
              className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md pl-9 pr-3 py-2.5 text-sm focus:border-[#FFBE00] focus:ring-1 focus:ring-[#FFBE00] outline-none"
              data-testid="listings-mobile-search"
            />
          </div>
          <button
            onClick={() => setFiltersOpen((x) => !x)}
            className="px-4 py-2.5 rounded-md border border-zinc-800 text-sm font-bold uppercase tracking-wider hover:border-[#FFBE00] hover:text-[#FFBE00]"
            data-testid="listings-mobile-filters-toggle"
          >
            <i className="fas fa-sliders mr-1"></i> Filtri{activeCat ? " ·1" : ""}
          </button>
        </div>
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Città (es. Roma, Milano…)"
          className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-3 py-2.5 text-sm focus:border-[#FFBE00] focus:ring-1 focus:ring-[#FFBE00] outline-none"
          data-testid="listings-mobile-city"
        />
        {filtersOpen && (
          <div className="hm-card p-4" data-testid="listings-mobile-cat-drawer">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Categorie</div>
            <button onClick={() => setCat("")} className={`w-full text-left px-3 py-2 text-sm rounded-md mb-1 ${!activeCat ? "bg-[#FFBE00] text-black font-semibold" : "text-zinc-300 hover:bg-zinc-900"}`}>Tutte</button>
            <div className="max-h-[280px] overflow-y-auto">
              {categories.map((c) => (
                <button key={c.slug} onClick={() => setCat(c.slug)} className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 ${activeCat === c.slug ? "bg-[#E619A8] text-white font-semibold" : "text-zinc-300 hover:bg-zinc-900"}`}>
                  <i className={`fas ${CATEGORY_ICON[c.slug]} text-xs w-4`}></i>
                  <span className="flex-1">{c.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 md:gap-8" id="results">
        {/* Sidebar filters (desktop only) */}
        <aside className="hidden md:block space-y-6" data-testid="listings-sidebar">
          <div className="hm-card p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Cerca</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Parola chiave…"
              className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-3 py-2 text-sm focus:border-[#FFBE00] focus:ring-1 focus:ring-[#FFBE00] outline-none"
              data-testid="listings-search-input"
            />
            <div className="text-xs uppercase tracking-wider text-zinc-500 mt-4 mb-3">Città</div>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Es. Roma, Milano…"
              className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-3 py-2 text-sm focus:border-[#FFBE00] focus:ring-1 focus:ring-[#FFBE00] outline-none"
              data-testid="listings-city-input"
            />
          </div>
          <div className="hm-card p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Categorie</div>
            <button
              onClick={() => setCat("")}
              className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${!activeCat ? "bg-[#FFBE00] text-black font-semibold" : "text-zinc-300 hover:bg-zinc-900"}`}
              data-testid="filter-cat-all"
            >
              Tutte
            </button>
            <div className="mt-1 max-h-[360px] overflow-y-auto">
              {categories.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setCat(c.slug)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 ${activeCat === c.slug ? "bg-[#E619A8] text-white font-semibold" : "text-zinc-300 hover:bg-zinc-900"}`}
                  data-testid={`filter-cat-${c.slug}`}
                >
                  <i className={`fas ${CATEGORY_ICON[c.slug]} text-xs w-4`}></i>
                  <span className="flex-1">{c.name}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div>
          <div className="text-sm text-zinc-500 mb-4" data-testid="listings-count">{loading ? "Caricamento…" : `${listings.length} annunci trovati`}</div>
          {!loading && listings.length === 0 && (
            <div className="hm-card p-10 text-center text-zinc-400" data-testid="listings-empty">
              <i className="fas fa-inbox text-4xl text-zinc-700 mb-3"></i>
              <div>Nessun annuncio trovato. Prova a cambiare filtri.</div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-5">
            {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
